import { getItemWithExpiration } from '@/lib/utils';
import {
    getCenterMemberListAPI,
    GetCenterMemberListRemainingPeriod,
    GetCenterMemberListRemainingSessions,
    GetCenterMemberListStatus,
    getCenterRemainingScheduleFee,
    getCenterRemainingScheduleSession,
    getCenterSalesSettlementCalendar,
    getCoachNameListAPI,
    getCoachPermission,
    getMemberNotificationSettingsAPI,
    getMemberScheduleHistory,
    getSessionReviewPermission,
} from '../api/center';

export const fetchGetCenterMemberList = (
    params: {
        centerId: string;
        memberStatus?: GetCenterMemberListStatus;
        remainingPeriod?: GetCenterMemberListRemainingPeriod;
        remainingSessions?: GetCenterMemberListRemainingSessions;
        coach?: string;
    },
    authorization: string
) => ({
    queryKey: [
        'getCenterMemberListQuery',
        params?.memberStatus,
        params?.remainingPeriod,
        params?.remainingSessions,
        params?.coach,
    ],
    queryFn: () =>
        getCenterMemberListAPI(
            params.centerId,
            params.memberStatus ?? 'all',
            params.remainingPeriod ?? 'all',
            params.remainingSessions ?? 'all',
            params.coach ?? 'all',
            authorization
        ),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.members ?? [];
    },
});

export const fetchGetMemberScheduleHistory = (
    params: {
        centerId?: string;
        coachId?: string;
        memberId?: string;
        date?: string;
    },
    authorization: string
) => ({
    queryKey: ['getMemberScheduleHistoryQuery', params?.centerId, params?.coachId, params?.memberId, params?.date],
    queryFn: async () =>
        await getMemberScheduleHistory(params.centerId, params.coachId, params.memberId, params.date, authorization),
    // onSuccess: (data: any) => {
    //     // 데이터 정제 로직
    //     return data?.data ?? undefined;
    // },
    staleTime: 0,
});

export const fetchCoachNameList = (params: { centerId: string; isAllConcat?: boolean; includeDeleted?: boolean }, authorization: string) => ({
    queryKey: ['getCoachListForCenterQuery', params.centerId, params.includeDeleted ?? false],
    queryFn: () => getCoachNameListAPI(params.centerId, authorization, params.includeDeleted),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        if (params.isAllConcat) {
            return dropDownCoachList(data?.data?.coachNameList) ?? [];
        } else {
            return dropDownCoachListWithAll(data?.data?.coachNameList) ?? [];
        }
    },
});

export const fetchSessionReviewPermission = (params: { centerId: string }, authorization: string) => ({
    queryKey: ['getSessionReviewPermissionQuery', params.centerId],
    queryFn: () => getSessionReviewPermission(params.centerId, authorization),
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data ?? {};
    },
});

export const fetchCenterSalesSettlementCalendar = (
    params: { centerId: string; targetMonth: string; targetCoach: string | 'all' },
    authorization: string
) => ({
    queryKey: ['getCenterSalesSettlementCalendarQuery', params.centerId, params.targetMonth, params.targetCoach],
    queryFn: () =>
        getCenterSalesSettlementCalendar(params.centerId, params.targetMonth, params.targetCoach, authorization),
    onSuccess: (data: any) => {
        return data?.data ?? {};
    },
});
export const fetchGetCenterRemainingScheduleFee = (
    params: {
        centerId: string;
        targetMonth: string;
        targetCoach: string;
    },
    authorization: string
) => ({
    queryKey: ['getCenterRemainingScheduleFeeQuery', params.centerId, params.targetMonth, params.targetCoach],
    queryFn: () =>
        getCenterRemainingScheduleFee(params.centerId, params.targetMonth, params.targetCoach, authorization),
    onSuccess: (data: any) => {
        return data?.data?.totalRemainingScheduleFee ?? undefined;
    },
});

export const fetchGetCenterRemainingScheduleSession = (
    params: {
        centerId: string;
        targetMonth: string;
        targetCoach: string;
    },
    authorization: string
) => ({
    queryKey: ['getCenterRemainingScheduleSessionQuery', params.centerId, params.targetMonth, params.targetCoach],
    queryFn: () =>
        getCenterRemainingScheduleSession(params.centerId, params.targetMonth, params.targetCoach, authorization),
    onSuccess: (data: any) => {
        return data?.data?.totalRemainSession ?? undefined;
    },
});
export const fetchCoachPermission = (params: { centerId: string }) => ({
    queryKey: ['getCoachPermissionQuery', params.centerId],
    queryFn: () => {
        const authorization = getItemWithExpiration('authorization');

        return getCoachPermission(params.centerId, authorization);
    },
    onSuccess: (data: any) => {
        // 데이터 정제 로직
        return data?.data ?? undefined;
    },
});

/**
 * 회원 알림 설정 쿼리 키.
 *
 * 🔴 centerId 는 반드시 넣는다 — 계정 전환 캐시 누수 원인이 centerId 없는 키였다(App.tsx `clearServerCache` 주석).
 *
 * `openSession` 키의 마지막 칸은 **모달이 열릴 때마다 새로 받는 id** 다(설계 규칙 15).
 * 열릴 때마다 키가 달라서, 이전에 연 모달의 200 캐시나 그때 시작해 아직 도는 요청이
 * 이번 열림의 "편집 가능" 판정에 섞이지 않는다. 센터 단위로 지우거나 무효화할 때는
 * `center(centerId)` 접두 키를 쓰면 모든 열림이 함께 잡힌다.
 */
export const memberNotificationSettingsKeys = {
    all: ['getMemberNotificationSettingsQuery'] as const,
    center: (centerId: string) => ['getMemberNotificationSettingsQuery', centerId] as const,
    openSession: (centerId: string, openSessionId: number) =>
        ['getMemberNotificationSettingsQuery', centerId, openSessionId] as const,
};

/**
 * 회원 알림 설정 조회 팩토리.
 *
 * queryFn 은 던지지 않는다 — 결과는 항상 `{ status, data }` 다. 그래서 404·403 도 React Query 에서는
 * success 로 보인다. 화면 단계는 `deriveMemberNotificationSettingsView` 로만 판정한다.
 *
 * `useSingleQuery` 로 쓰지 않는다: `keepPreviousData: true` 가 고정이고 전역 `staleTime` 1분을 바꿀 수 없어
 * 재오픈 시 이전 200 캐시가 먼저 보인다(설계 7-4). `useMemberNotificationSettings` 훅이 전용 옵션으로 쓴다.
 */
export const fetchMemberNotificationSettings = (params: { centerId: string; openSessionId: number }) => ({
    queryKey: memberNotificationSettingsKeys.openSession(params.centerId, params.openSessionId),
    queryFn: () => {
        // 토큰은 요청 시점에 읽는다 (fetchCoachPermission 과 같다)
        const authorization = getItemWithExpiration('authorization');
        return getMemberNotificationSettingsAPI(params.centerId, authorization);
    },
});

export // all, 전체 포함 시켜서 리턴
const dropDownCoachListWithAll = (coachListData: GetCoachName[]) =>
    [{ code: 'all', value: '전체' }].concat(
        (coachListData ?? []).map((coach: GetCoachName) => ({
            code: coach.coachId,
            value: coach.name,
            object: coach,
        }))
    );
export // all, 전체 포함 시켜서 리턴
const dropDownCoachList = (coachListData: GetCoachName[]) =>
    (coachListData ?? []).map((coach: GetCoachName) => ({
        code: coach.coachId,
        value: coach.name,
        object: coach,
    }));

export type GetCoachName = {
    coachId: string;
    name: string;
    profileImageUrl?: string;
    isDeleted?: boolean;
};

export type DaySchedule = {
    [day: string]: {
        schedules: MemberClassHistorySchedule[];
        dayScheduleCount: number;
    }[];
};

export type MonthSchedule = {
    [month: string]: DaySchedule[];
};

export type YearSchedule = {
    [year: string]: MonthSchedule;
};

export type MonthlySchedule = {
    success: boolean;
    // PT
    thisMonthScheduleCount: number;
    thisYearScheduleCount: number;
    allScheduleCount: number;
    thisMonth: MemberClassHistorySchedule[];
    thisYear: YearSchedule;
    totalView: YearSchedule[];
    // 그룹 (옵션 A)
    thisMonthBookingCount?: number;
    thisYearBookingCount?: number;
    allBookingCount?: number;
    thisMonthBookings?: MemberBookingHistory[];
    thisYearBookings?: YearSchedule;
    totalViewBookings?: YearSchedule[];
    // 통합 (B안) — PT + 그룹을 UnifiedHistoryItem으로 정규화해서 머지
    thisMonthAllCount?: number;
    thisYearAllCount?: number;
    allCount?: number;
    thisMonthAll?: UnifiedHistoryItem[];
    thisYearAll?: YearSchedule;
    totalViewAll?: YearSchedule[];
};

export type UnifiedHistoryItem = {
    _id: string;
    source: 'pt' | 'group';
    /** PT Schedule._id or Group Booking.scheduleId depending on source */
    scheduleId?: string;
    /** 그룹 row 전용 — GroupSchedule._id (popover 열기에 사용) */
    sessionId?: string;
    groupScheduleId?: string;
    groupSessionId?: string;
    bookingId?: string;
    startTime: string;
    endTime: string;
    coachId: string;
    coachName: string;
    title: string;
    status: 'ATTENDANCE' | 'NOSHOW' | 'UPCOMING' | 'CANCELLED' | 'WAITLISTED';
    membershipId?: string;
    memberTicketId?: string;
    memberId?: string;
    ticketName?: string;
    currentSession?: number;
    totalSession?: number;
    bookedBy?: 'member' | 'admin' | 'coach';
    createdAt?: string;
};

export type MemberBookingHistory = {
    _id: string;
    sessionId: string;
    sessionTitle: string;
    coachId: string;
    coachName: string;
    startTime: string;
    endTime: string;
    status: 'CONFIRMED' | 'COMPLETED' | 'NOSHOW' | 'CANCELLED' | 'WAITLISTED';
    ticketId?: string;
    ticketName?: string;
    memberTicketId?: string;
    bookedBy?: 'member' | 'admin' | 'coach';
    // PT와 동일 렌더러 재사용을 위한 보조 필드 (서버가 세팅하거나 프론트에서 매핑)
    attendanceStatus?: 'ATTENDANCE' | 'NOSHOW';
    membershipId?: string;
    membershipName?: string;
    currentSession?: number;
    totalSession?: number;
};

export type MemberClassHistorySchedule = {
    scheduleBundleId: string | null;
    memo: string;
    conditionId: string;
    postId: string;
    membershipId: string;
    attendanceStatus: 'NOSHOW' | 'ATTENDANCE' | 'ABSENT';
    isDeleted: boolean;
    isCanceled: boolean;
    coachId: string;
    memberId: string;
    alias: string;
    startTime: string;
    endTime: string;
    requestDeadline: string;
    issuer: {
        issuerId: string;
        issuerRole: 'center' | 'coach' | 'admin';
    };
    createdAt: string;
    updatedAt: string;
    __v: number;
    id: string;
    currentSession: number;
    totalSession: number;
    membershipCount: number;
    membershipName: string;
};

// 월별 총 합계 타입
type MonthlyTotal = {
    monthlyIndex: number; // 1~12: 몇 월인지
    totalSales: number; // 총 매출액
    totalScheduleFee: number; // 수업료
    totalRemainingScheduleFee: number; // 미수업 금액
    totalPaidScheduleCount: number; // 결제 수업 수
    totalScheduleCount: number; // 잡힌 수업 수
    totalNoshowScheduleCount: number; // 노쇼 수업 수
};

// 주별 합계 타입
export type WeeklyTotal = {
    weeklyIndex: number; // 몇 주차인지 1~6
    weeklySales: number; // 매출금액
    weeklyPaidScheduleCount: number; // 결제 수업 수
    weeklyScheduleCount: number; // 확정 수업 수
    weeklyNoshowScheduleCount: number; // 노쇼 수업 수
};

// 매출 금액 상세 타입
type SalesInfo = {
    paidSalesCount: number; // 결제 건수
    cancelSalesCount: number; // 취소 건수
    paidSales: number; // 결제 금액
    cancelSales: number; // 취소 금액
    sales: number; // 매출 금액
};

// 결제 수업 상세 타입
type PaidScheduleInfo = {
    paidScheduleCount: number; // 결제 등록 수업 수
    paidMemberCount: number; // 결제 회원 수
    newPaymentCount: number; // 신규 결제 수
    reRegistrationPaymentCount: number; // 재등록 결제 수
    partialPaymentCount: number; // 분할 결제 수
};

// 완료한 수업 상세 타입
type ScheduleCountInfo = {
    scheduleCount: number; // 총 수업 수
    attendanceScheduleCount: number; // 출석 완료 수업 수
    noshowScheduleCount: number; // 노쇼 수업 수
};

// 일별 정보 타입
export type DailyInfo = {
    start: string; // 며칠인지 1~31
    end: string; // 며칠인지 1~31
    salesInfo: SalesInfo; // 매출 금액 상세
    paidScheduleInfo: PaidScheduleInfo; // 결제 수업 상세
    scheduleCountInfo: ScheduleCountInfo; // 완료한 수업 상세
};

// 메인 데이터 타입
export type SalesSummary = {
    monthlyTotal: MonthlyTotal; // 월별 총 합계
    weeklyTotals: WeeklyTotal[]; // 주별 합계 배열
    dailyInfos: DailyInfo[]; // 일별 정보 배열
};
